<script setup lang="ts">
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'
import type { ContentEntity } from '@bf/domain'
import { useShowroomStore } from '../store/showroom'

const props = defineProps<{
  entity: ContentEntity
}>()
const store = useShowroomStore()
const coverFailed = ref(false)

defineEmits<{
  add: [entity: ContentEntity]
}>()

const label = computed(() => {
  if (props.entity.contentType === 'product') {
    if (props.entity.productCategory === 'cabinet') return '机柜'
    if (props.entity.productCategory === 'ddc-controller') return 'DDC'
    return '末端设备'
  }
  if (props.entity.contentType === 'software-style') {
    return '完整风格包'
  }
  if (props.entity.contentType === 'software-component') return '软件组件'
  if (props.entity.contentType === 'project-case') return '工程案例'
  return '公司资料'
})

const cardCode = computed(() => {
  if (props.entity.contentType === 'product') return 'HW'
  if (props.entity.contentType === 'software-style') return 'UI'
  if (props.entity.contentType === 'software-component') return 'CP'
  if (props.entity.contentType === 'project-case') return 'CS'
  return 'BF'
})

const detailsTo = computed(() => {
  if (props.entity.contentType === 'software-style') {
    return { name: 'software-demo', params: { id: props.entity.id } }
  }
  return { name: 'item', params: { id: props.entity.id } }
})

const isSelectedStyle = computed(
  () =>
    props.entity.contentType === 'software-style' &&
    (store.state.draft?.items.some(
      (item) =>
        item.contentType === 'software-style' &&
        item.entityId === props.entity.id
    ) ?? false)
)

const coverAsset = computed(() =>
  props.entity.mediaIds
    .map((id) => store.getAsset(id))
    .filter(
      (asset) =>
        asset?.mediaType === 'image' &&
        (asset.displayRole === 'cover' || asset.displayRole === 'gallery')
    )
    .sort((left, right) => {
      if (left?.displayRole !== right?.displayRole) {
        return left?.displayRole === 'cover' ? -1 : 1
      }
      return Number(right?.preferred) - Number(left?.preferred)
    })[0]
)

</script>

<template>
  <article class="entity-card">
    <div class="entity-visual" :data-kind="entity.contentType">
      <img
        v-if="coverAsset && !coverFailed"
        :src="store.mediaUrl(coverAsset)"
        :alt="coverAsset.altText"
        loading="lazy"
        @error="coverFailed = true"
      />
      <template v-else>
        <span>{{ cardCode }}</span>
        <small>{{ entity.systemDomains.join(' / ').toUpperCase() }}</small>
      </template>
      <b v-if="coverAsset?.conceptVisual" class="concept-badge">概念示意</b>
    </div>
    <div class="entity-body">
      <div class="entity-heading">
        <span class="badge">{{ label }}</span>
        <span v-if="'demoData' in entity && entity.demoData" class="demo-flag">
          虚构数据
        </span>
      </div>
      <h3>{{ entity.title }}</h3>
      <p>{{ entity.summary }}</p>
      <div class="entity-tags">
        <span v-for="tag in entity.tags.slice(0, 3)" :key="tag">{{ tag }}</span>
      </div>
      <div class="entity-actions">
        <RouterLink class="button button-secondary" :to="detailsTo">
          查看详情
        </RouterLink>
        <button
          class="button button-ghost"
          :class="{ 'button-selected': isSelectedStyle }"
          type="button"
          :aria-pressed="entity.contentType === 'software-style' ? isSelectedStyle : undefined"
          @click="$emit('add', entity)"
        >
          {{
            entity.contentType === 'software-style'
              ? isSelectedStyle
                ? '✓ 当前已选'
                : '选用风格'
              : '＋ 加入方案'
          }}
        </button>
      </div>
    </div>
  </article>
</template>
